/**
 * Frontière DOM du compte à rebours : lit le balisage, pilote la boucle rAF et
 * orchestre la chorégraphie (countdown-motion.js) à partir de la logique pure
 * du domaine (countdown.js).
 * Transcription de dpc-timer : src/components/organisms/CountdownTimer.client.ts
 *
 * Ajouts propres à la page password Shopify :
 * - heure de référence = celle de Shopify, pas l'horloge du visiteur (souvent décalée) ;
 * - à t0, bascule sur la boutique dès que le verrou est levé dans l'admin.
 */

import {
  isFinalCountdown,
  isFinalCountdownApproaching,
  isUnitExpired,
  remainingUntil,
  ringFraction,
  UNITS,
} from "./countdown.js";
import { launchFireworks } from "./fireworks.js";
import {
  bounceFinalNumber,
  collapseUnit,
  enlargeFinalNumber,
  FINAL_LEAD_MS,
  finalFontPx,
  handOffSeconds,
  playOpening,
  prepareFinalSpace,
} from "./countdown-motion.js";
import "./flip-card.js";

// ──────────────── Heure serveur ─────────────────────────────────────────────
// Le rendu Liquid ne convient pas ('now' est figé au dernier rendu, page en cache) : on lit
// l'en-tête Date de quelques requêtes same-origin. Date n'a qu'une précision d'une seconde :
// chaque mesure borne le décalage, l'intersection des bornes le resserre sous la seconde.
// Le temps écoulé ensuite est mesuré par performance.now() (monotone, insensible à un
// changement d'heure du poste).

// Mesures d'affinage lancées après la première (qui, seule, conditionne l'affichage)
const REFINE_SAMPLES = 2;
// Au-delà, on affiche sur l'horloge du poste sans attendre la première mesure
const FIRST_SAMPLE_TIMEOUT_MS = 1000;

// Décalage estimé heure serveur - performance.now(), en ms (null = horloge du poste)
let serverOffset = null;
// Bornes du décalage, resserrées à chaque mesure
let bounds = null;

async function sampleServerTime() {
  const sent = performance.now();
  // robots.txt : réponse dynamique (Date frais) mais sans rendu de page, deux fois plus rapide
  // que /password ; un aller-retour court resserre aussi les bornes
  const response = await fetch("/robots.txt", { method: "HEAD", cache: "no-store" });
  const received = performance.now();
  const date = Date.parse(response.headers.get("Date") ?? "");
  if (Number.isNaN(date)) throw new Error("En-tête Date absent");
  // L'heure serveur était dans [date, date + 1000[ à un instant de [sent, received]
  return { min: date - received, max: date + 1000 - sent };
}

async function addSample() {
  const sample = await sampleServerTime();
  const min = Math.max(bounds?.min ?? -Infinity, sample.min);
  const max = Math.min(bounds?.max ?? Infinity, sample.max);
  // Bornes incohérentes (serveurs aux horloges légèrement différentes) : on repart de la mesure
  bounds = min <= max ? { min, max } : sample;
  serverOffset = (bounds.min + bounds.max) / 2;
}

async function refine() {
  for (let i = 0; i < REFINE_SAMPLES; i++) await addSample();
}

async function resync() {
  bounds = null;
  await addSample();
  await refine();
}

const serverNow = () => (serverOffset === null ? Date.now() : performance.now() + serverOffset);

// Première mesure (un aller-retour) avant l'affichage : pas de saut visible pour un poste
// décalé ; les suivantes affinent en arrière-plan (correction sous la demi-seconde).
// L'ouverture attend l'issue de la première : un poste en avance ne doit pas la déclencher.
const firstSampleDone = addSample().catch((err) => {
  // Horloge du visiteur en secours : le décompte reste juste à son décalage près
  console.error("Synchronisation de l'heure serveur impossible :", err);
});
firstSampleDone.then(() => (serverOffset === null ? null : refine())).catch(() => {});

let firstSyncDone = false;
const firstSample = Promise.race([
  firstSampleDone,
  new Promise((resolve) => setTimeout(resolve, FIRST_SAMPLE_TIMEOUT_MS)),
]);
firstSampleDone.then(() => {
  firstSyncDone = true;
});

// ──────────────── Ouverture de la boutique ──────────────────────────────────
// La boutique s'ouvre quand le verrou est levé dans l'admin. On sonde / chaque seconde sans
// suivre les redirections : tant qu'il redirige vers /password, c'est encore fermé.

const POLL_MS = 1000;
// Au-delà, le verrou n'a visiblement pas été levé à l'heure : on sonde moins souvent
const POLL_SLOW_AFTER_MS = 5 * 60 * 1000;
const POLL_SLOW_MS = 10 * 1000;

async function isStoreOpen() {
  try {
    const response = await fetch("/", { redirect: "manual", cache: "no-store" });
    // Verrouillé : 302 vers /password, qui donne une réponse opaqueredirect
    return response.type !== "opaqueredirect" && response.ok;
  } catch {
    return false;
  }
}

async function waitForStoreOpen() {
  const startedAt = performance.now();
  while (!(await isStoreOpen())) {
    const waited = performance.now() - startedAt;
    await new Promise((resolve) => setTimeout(resolve, waited > POLL_SLOW_AFTER_MS ? POLL_SLOW_MS : POLL_MS));
  }
}

// Temps laissé au feu d'artifice seul, après l'agrandissement du logo, avant la bascule
const FIREWORKS_HOLD_MS = 5000;

/** Attend la fin de l'agrandissement du logo (lancé sans attente par playOpening), puis le maintien. */
async function holdOpening(logo) {
  await Promise.all(logo.getAnimations().map((animation) => animation.finished));
  await new Promise((resolve) => setTimeout(resolve, FIREWORKS_HOLD_MS));
}

// En test, le verrou n'est pas levé : au moment de la bascule, la page s'agrandit en douceur à
// la place de la navigation, pour situer l'instant.
const DEV_TRANSITION_SCALE = 1.5;
const DEV_TRANSITION = { duration: 1200, easing: "cubic-bezier(0.65, 0, 0.35, 1)", fill: "forwards" };

function playDevTransition() {
  document.documentElement.style.overflow = "hidden";
  const body = document.body;
  body.style.transformOrigin = `${window.innerWidth / 2}px ${window.scrollY + window.innerHeight / 2}px`;
  body.animate([{ transform: "scale(1)" }, { transform: `scale(${DEV_TRANSITION_SCALE})` }], DEV_TRANSITION);
}

// ──────────────── Compte à rebours ──────────────────────────────────────────

const pad = (value) => value.toString().padStart(2, "0");

/** Élément requis par le balisage : son absence est un bug, pas un cas métier. */
function required(root, selector, type) {
  const element = root.querySelector(selector);
  if (!(element instanceof type)) throw new Error(`CountdownTimer : élément ${selector} introuvable`);
  return element;
}

/** Instancie le message d'ouverture depuis son <template>. */
function cloneMessage(root) {
  const template = required(root, "[data-opening-message]", HTMLTemplateElement);
  const message = template.content.firstElementChild?.cloneNode(true);
  if (!(message instanceof HTMLElement)) throw new Error("CountdownTimer : message d'ouverture vide");
  return message;
}

/** Test : éditeur de thème et sous-domaine dev. (data-dev), ou `shopify theme dev`. */
const isDevMode = (root) => "dev" in root.dataset || ["127.0.0.1", "localhost"].includes(window.location.hostname);

/**
 * Cible du décompte. En test (data-dev : éditeur de thème, sous-domaine dev. ; ou
 * `shopify theme dev`), `?remaining=<secondes>` la remplace pour tester chaque
 * transition sans attendre (ex. 86405 → chute des jours dans 5 s). Inactif sur l'apex.
 */
function resolveTarget(root) {
  const isoTarget = root.dataset.target ?? "";
  const parsed = new Date(isoTarget).getTime();
  if (Number.isNaN(parsed)) throw new Error(`CountdownTimer : date cible invalide « ${isoTarget} »`);
  if (!isDevMode(root)) return parsed;
  const override = Number(new URLSearchParams(window.location.search).get("remaining"));
  return override > 0 ? serverNow() + override * 1000 : parsed;
}

function mountCountdownTimer(root) {
  const ring = required(root, "[data-ring]", SVGCircleElement);
  const row = required(root, "[data-units]", HTMLElement);
  const secondsBlock = required(root, '[data-duration-unit="seconds"]', HTMLElement);
  const cells = {
    days: required(root, '[data-unit="days"]', HTMLElement),
    hours: required(root, '[data-unit="hours"]', HTMLElement),
    minutes: required(root, '[data-unit="minutes"]', HTMLElement),
    seconds: required(root, '[data-unit="seconds"]', HTMLElement),
  };
  const opening = {
    root,
    status: required(root, "[data-status]", HTMLElement),
    message: cloneMessage(root),
    ringTrack: required(root, "[data-ring-track]", SVGSVGElement),
    logo: required(root, "[data-logo]", HTMLElement),
  };
  const target = resolveTarget(root);
  const motion = !window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const collapsed = new Set();
  const shown = {
    days: "",
    hours: "",
    minutes: "",
    seconds: "",
  };
  let isFirstFrame = true;
  let handOffStarted = false;
  let enlarged = false;
  let finalNumber = null;
  let finalSizePx = 0;
  let currentSeconds = 0;

  // L'anneau est repeint à chaque frame (fluide) ; chaque cellule n'est
  // réécrite qu'au changement de sa valeur (point d'accroche du flip clock).
  const frame = () => {
    const remaining = remainingUntil(target, serverNow());
    ring.style.strokeDashoffset = String(1 - ringFraction(remaining.totalMs));

    if (remaining.totalMs <= 0 && !firstSyncDone) {
      requestAnimationFrame(frame);
      return;
    }
    if (remaining.totalMs <= 0) {
      // Bascule sur la boutique une fois l'ouverture jouée jusqu'au bout (logo agrandi, puis
      // feu d'artifice seul) et le verrou levé : rien n'est coupé, même verrou levé en avance
      const opened = playOpening(opening, motion).then(() => holdOpening(opening.logo));
      if (isDevMode(root)) opened.then(playDevTransition);
      else Promise.all([opened, waitForStoreOpen()]).then(() => window.location.replace("/"));
      void launchFireworks();
      return;
    }

    // Au chargement, les unités déjà expirées disparaissent sans animation.
    const animate = !isFirstFrame && motion;
    for (const unit of UNITS) {
      if (collapsed.has(unit) || !isUnitExpired(unit, remaining.totalMs)) continue;
      collapsed.add(unit);
      void collapseUnit(row, unit, animate);
    }

    currentSeconds = remaining.seconds;
    // Le passage de relais démarre en avance pour être fini avant le « 10 ».
    if (!handOffStarted && isFinalCountdownApproaching(remaining.totalMs, FINAL_LEAD_MS)) {
      handOffStarted = true;
      finalSizePx = finalFontPx();
      prepareFinalSpace(secondsBlock, cells.seconds, root, finalSizePx, animate);
      void handOffSeconds(cells.seconds, secondsBlock, animate).then((number) => {
        number.textContent = String(currentSeconds);
        finalNumber = number;
      });
    }

    if (finalNumber && !enlarged && isFinalCountdown(remaining.totalMs)) {
      enlarged = true;
      enlargeFinalNumber(finalNumber, finalSizePx, animate);
    }

    for (const unit of UNITS) {
      const value = pad(remaining[unit]);
      if (value === shown[unit]) continue;
      shown[unit] = value;

      // Chiffre nu sans zéro devant (12, 11, 10…) ; bounce à partir du « 10 ».
      if (unit === "seconds" && finalNumber) {
        finalNumber.textContent = String(remaining.seconds);
        if (isFinalCountdown(remaining.totalMs) && motion) bounceFinalNumber(finalNumber);
        continue;
      }

      // Pousse la valeur sur <flip-card> ; l'atome déclenche le flip.
      cells[unit].dataset.value = value;
    }

    isFirstFrame = false;
    requestAnimationFrame(frame);
  };

  requestAnimationFrame(frame);
}

const root = document.querySelector("[data-countdown-root]");
if (!(root instanceof HTMLElement)) throw new Error("CountdownTimer : racine [data-countdown-root] introuvable");

// Certains navigateurs figent performance.now() pendant la veille du poste : recalage au retour
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") resync().catch(() => {});
});

await firstSample;
mountCountdownTimer(root);
